import React from "react";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  Typography,
} from "@mui/material";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useLocation, useNavigate } from "react-router-dom";
import { PANEL_SIDEBAR } from "../../design-system/panel/panelSidebarTokens";
import { PANEL } from "../../design-system/panel/panelTokens";
import { isPathActive } from "../../config/navigation/navUtils";
import { panelSidebarNavItemLabelSx } from "./panelSidebarNav";

const ROUTE_PREFETCH_MAP = {
  "/dashboard": () => import("../../scenes/dashboard/index.jsx"),
  "/team": () => import("../../scenes/team/index.jsx"),
  "/patients": () => import("../../scenes/patients/index.jsx"),
  "/files": () => import("../../scenes/files/index.jsx"),
  "/doctor-permission-groups": () => import("../../scenes/doctor-permission-groups/PermissionGroupsList.jsx"),
  "/collaboration-groups": () => import("../../scenes/collaboration-groups/CollaborationGroupsList.jsx"),
  "/inviteDoctor": () => import("../../scenes/invite-doctor/InviteDoctor.jsx"),
  "/AddPatient": () => import("../../scenes/addpatient/index.jsx"),
  "/statistical-analysis": () => import("../../scenes/statistical-analysis/index.jsx"),
  "/feature-impact": () => import("../../scenes/feature-impact/index.jsx"),
  "/machine-learning": () => import("../../scenes/machine-learning/index.jsx"),
  "/media-vision": () => import("../../scenes/mediavision/index.jsx"),
  "/calendar": () => import("../../scenes/calendar/index.jsx"),
  "/form-market": () => import("../../scenes/form-market/index.jsx"),
  "/formulas": () => import("../../scenes/formulas/index.jsx"),
  "/excel-import": () => import("../../scenes/excel-import/index.jsx"),
  "/faq": () => import("../../scenes/faq/index.jsx"),
  "/doctorProfile": () => import("../../scenes/profile/doctorProfile.jsx"),
  "/profile": () => import("../../scenes/profile/tenantProfile.jsx"),
  "/tenant/dashboard": () => import("../../scenes/dashboard/TenDashboard.jsx"),
  "/tenant/subscription": () => import("../../scenes/subscription/TenantSubscription.jsx"),
  "/tenant/doctors": () => import("../../scenes/tenant-doctors/index.jsx"),
  "/tenant/invite-admin-doctor": () => import("../../scenes/tenant-invite-doctor/InviteAdminDoctor.jsx"),
  "/tenant/patients": () => import("../../scenes/tenant-patients/index.jsx"),
  "/tenant/permission-groups": () => import("../../scenes/tenant-permission-groups/PermissionGroupsList.jsx"),
};

const prefetchedRoutes = new Set();

function prefetchRoute(path) {
  if (!path || prefetchedRoutes.has(path)) return;
  const loader = ROUTE_PREFETCH_MAP[path];
  if (typeof loader === "function") {
    prefetchedRoutes.add(path);
    loader().catch(() => {});
  }
}

/**
 * Ortak panel sidebar — saf UI bileşeni.
 * Navigasyon config dışarıdan gelir; renkler PANEL_SIDEBAR SSOT'tan okunur.
 */
export default function PanelSidebar({
  menuOpen,
  setMenuOpen,
  variant = "temporary",
  drawerWidth = PANEL.drawer.width,
  collapsedWidth = PANEL.drawer.collapsed,
  navSections = [],
  profileItem = null,
  pathOptions = {},
  brand = {},
  beforeNav = null,
  showSectionLabels = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const SIDEBAR = PANEL_SIDEBAR;
  const isPersistent = variant === "persistent";
  const isMobileDrawer = variant === "temporary";
  const isCollapsed = isPersistent && !menuOpen;
  const currentWidth = isCollapsed ? collapsedWidth : drawerWidth;

  const {
    collapsedPrimary = "P",
    collapsedAccent = "M",
    titlePrimary = "Patient",
    titleAccent = "Mesh",
    subtitle = "",
    mobileTitle = "",
    mobileSubtitle = "",
    accentColor,
    titleColor,
  } = brand;

  const brandAccent = accentColor || titleColor;

  const renderNavItem = (item) => {
    const isActive = isPathActive(location.pathname, item.path, pathOptions);
    const listItem = (
      <ListItemButton
        onClick={() => {
          navigate(item.path);
          if (!isPersistent) setMenuOpen(false);
        }}
        onMouseEnter={() => prefetchRoute(item.path)}
        onFocus={() => prefetchRoute(item.path)}
        sx={{
          borderRadius: PANEL.radius.input,
          py: 1.1,
          px: isCollapsed ? 1 : 1.5,
          mb: 0.35,
          justifyContent: isCollapsed ? "center" : "flex-start",
          alignItems: isCollapsed ? "center" : "flex-start",
          color: isActive ? SIDEBAR.textActive : SIDEBAR.text,
          backgroundColor: isActive ? SIDEBAR.activeBg : "transparent",
          transition:
            "background-color 0.15s ease, color 0.15s ease, padding 0.2s ease",
          "&:hover": {
            backgroundColor: isActive ? SIDEBAR.activeBg : SIDEBAR.hoverBg,
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: isCollapsed ? 0 : 36,
            justifyContent: "center",
            color: isActive ? SIDEBAR.accent : SIDEBAR.textMuted,
            mt: isCollapsed ? 0 : 0.15,
          }}
        >
          {item.Icon ? <item.Icon sx={{ fontSize: 22 }} /> : null}
        </ListItemIcon>
        {!isCollapsed && (
          <Typography
            component="span"
            sx={{
              ...panelSidebarNavItemLabelSx,
              fontWeight: isActive ? 600 : 500,
              color: "inherit",
            }}
          >
            <span style={item.comingSoon ? { filter: "blur(0.6px)", opacity: 0.65 } : undefined}>{item.label}</span>
            {item.comingSoon && <span style={{ display: "inline-flex", alignItems: "center", marginLeft: 8,
              padding: "2px 7px", borderRadius: 999, fontSize: "0.68em", fontWeight: 700,
              lineHeight: 1.5, whiteSpace: "nowrap", color: "#92700c", backgroundColor: "#fff7db",
              border: "1px solid #eadca7", verticalAlign: "middle" }}>Yakında</span>}
          </Typography>
        )}
      </ListItemButton>
    );

    if (isCollapsed) {
      return (
        <Tooltip key={item.id || item.path} title={item.label} placement="right" arrow>
          {listItem}
        </Tooltip>
      );
    }

    return <React.Fragment key={item.id || item.path}>{listItem}</React.Fragment>;
  };

  const drawerPaperSx = {
    width: isPersistent ? currentWidth : { xs: 280, sm: drawerWidth },
    background: SIDEBAR.bg,
    borderRight: `1px solid ${SIDEBAR.border}`,
    boxShadow: isPersistent ? "none" : PANEL.shadow.drawerMobile,
    transition: "width 0.28s ease",
    overflowX: "hidden",
  };

  return (
    <Drawer
      anchor="left"
      open={isPersistent ? true : menuOpen}
      onClose={() => setMenuOpen(false)}
      variant={variant}
      ModalProps={variant === "temporary" ? { keepMounted: true } : undefined}
      sx={{
        display: isPersistent ? { xs: "none", md: "block" } : undefined,
        "& .MuiDrawer-paper": drawerPaperSx,
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Brand + toggle */}
        <Box
          sx={{
            px: isCollapsed ? 1 : 2.5,
            pt: 2,
            pb: isCollapsed ? 1.5 : 2,
            display: "flex",
            alignItems: isCollapsed ? "center" : "flex-start",
            justifyContent: isCollapsed ? "center" : "space-between",
            flexDirection: isCollapsed ? "column" : "row",
            gap: 1,
            minHeight: isCollapsed ? 88 : "auto",
          }}
        >
          {isCollapsed ? (
            <Typography
              component="div"
              sx={{
                fontWeight: 800,
                letterSpacing: "-0.03em",
                color: titleColor || PANEL.text.primary,
                fontSize: "1.1rem",
                lineHeight: 1,
              }}
            >
              {collapsedPrimary}
              <Box component="span" sx={{ color: brandAccent || PANEL.accent.main }}>
                {collapsedAccent}
              </Box>
            </Typography>
          ) : isMobileDrawer && mobileTitle ? (
            <Box
              sx={{
                minWidth: 0,
                flex: 1,
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  backgroundColor: PANEL.blue.soft12,
                  color: PANEL.blue.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <DashboardCustomizeOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="h6"
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "-0.02em",
                    color: titleColor || PANEL.text.primary,
                    lineHeight: 1.2,
                    fontSize: "1.05rem",
                  }}
                >
                  {mobileTitle}
                </Typography>
                {mobileSubtitle ? (
                  <Typography
                    variant="caption"
                    sx={{
                      color: SIDEBAR.textMuted,
                      display: "block",
                      mt: 0.35,
                      fontSize: "0.72rem",
                      lineHeight: 1.35,
                    }}
                  >
                    {mobileSubtitle}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ) : (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                component="h6"
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "-0.03em",
                  color: titleColor || PANEL.text.primary,
                  lineHeight: 1.2,
                  fontSize: "1.05rem",
                  whiteSpace: "nowrap",
                }}
              >
                {titlePrimary}
                <Box component="span" sx={{ color: brandAccent || PANEL.accent.main }}>
                  {titleAccent}
                </Box>
              </Typography>
              {subtitle ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: SIDEBAR.textMuted,
                    display: "block",
                    mt: 0.35,
                    fontSize: "0.72rem",
                    lineHeight: 1.35,
                    whiteSpace: "nowrap",
                  }}
                >
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
          )}

          {isPersistent ? (
            <Tooltip
              title={isCollapsed ? "Menüyü genişlet" : "Menüyü daralt"}
              placement="right"
            >
              <IconButton
                onClick={() => setMenuOpen((prev) => !prev)}
                size="small"
                aria-label={isCollapsed ? "expand menu" : "collapse menu"}
                sx={{
                  color: SIDEBAR.textMuted,
                  alignSelf: isCollapsed ? "center" : "flex-start",
                  "&:hover": { backgroundColor: SIDEBAR.hoverBg },
                }}
              >
                {isCollapsed ? (
                  <ChevronRightIcon sx={{ fontSize: 20 }} />
                ) : (
                  <ChevronLeftIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton
              onClick={() => setMenuOpen(false)}
              size="small"
              aria-label="close menu"
              sx={{
                color: SIDEBAR.textMuted,
                mt: -0.25,
                "&:hover": { backgroundColor: SIDEBAR.hoverBg },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>

        <Divider sx={{ borderColor: SIDEBAR.border, mx: isCollapsed ? 1 : 2 }} />

        {beforeNav ? (
          <Box sx={{ px: isCollapsed ? 0.75 : 1.5, pt: 1.5 }}>{beforeNav}</Box>
        ) : null}

        {/* Navigation */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            py: 1.5,
            px: isCollapsed ? 0.75 : 1.5,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: SIDEBAR.scrollbarThumb,
              borderRadius: 8,
            },
          }}
        >
          {navSections.map((section, sectionIndex) => (
            <Box
              key={section.id}
              sx={{ mb: sectionIndex < navSections.length - 1 ? 1.5 : 0 }}
            >
              {showSectionLabels && section.label && !isCollapsed ? (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    px: 1.5,
                    mb: 0.75,
                    color: SIDEBAR.sectionLabel,
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    letterSpacing: 0.2,
                    textTransform: "none",
                  }}
                >
                  {section.label}
                </Typography>
              ) : null}
              <List dense disablePadding component={isCollapsed ? "div" : "ul"}>
                {section.items.map((item) => renderNavItem(item))}
              </List>
            </Box>
          ))}
        </Box>

        {/* Profile footer */}
        {profileItem ? (
          <Box sx={{ px: isCollapsed ? 0.75 : 1.5, pb: 2.5, pt: 0.5 }}>
            <Divider sx={{ borderColor: SIDEBAR.border, mb: 1.25 }} />
            <List dense disablePadding component={isCollapsed ? "div" : "ul"}>
              {renderNavItem(profileItem)}
            </List>
          </Box>
        ) : null}
      </Box>
    </Drawer>
  );
}
